import { DataGrid } from "@msuite/ui/data-grid";
import { useTranslation } from "@msuite/ui/localization";
import { formatNumber, getHoursString } from "@msuite/utils/numbers";
import { calculateBookings } from "@msuite/utils/partnerships";
import type { AssignmentReferenceBookingsSchema } from "@msuite/utils/schemas";
import type { Nullable, Tables } from "@msuite/utils/types";
import { useFormContext, useWatch } from "react-hook-form";

/** Props Interface */
interface AssignmentReferenceBookingsSettlementProps {
	agreement: Nullable<Tables<"organizationPartnershipAgreements">>;
	inputRole: "partner" | "host";
}

export function AssignmentReferenceBookingsSettlement({
	agreement,
	inputRole,
}: AssignmentReferenceBookingsSettlementProps) {
	/** Context */
	const { t } = useTranslation();

	/** Context */
	const methods = useFormContext<AssignmentReferenceBookingsSchema>();
	const bookings = useWatch({ control: methods.control, name: "bookings" });
	const adjustmentBookings = bookings
		?.flatMap((booking) => booking.adjustment)
		?.filter((item) => !!item);
	const _bookings = inputRole === "partner" ? bookings : adjustmentBookings;

	/** Hooks */
	const { submittedSettlement: settlement, submittedHours: hours } = calculateBookings(
		_bookings,
		agreement?.value,
	);

	/** Render */
	return (
		<div className="w-full">
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("partners.bookings")}</DataGrid.Label>
					<DataGrid.Value justify="end">{formatNumber(bookings.length, "integer")}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>
						{inputRole === "partner"
							? t("partners.submitted_hours")
							: t("partners.controlled_hours")}
					</DataGrid.Label>
					<DataGrid.Value justify="end">{getHoursString(hours)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("partners.hourly_rate")}</DataGrid.Label>
					<DataGrid.Value justify="end">
						{formatNumber(agreement?.value, "currency")}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("partners.settlement_sum")}</DataGrid.Label>
					<DataGrid.Value justify="end">{formatNumber(settlement, "currency")}</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
		</div>
	);
}
